import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { useRef, useEffect, useState } from "react";
import ModuleHeader from "./ModuleHeader";
import ReactEcharts from "echarts-for-react";
import { eamApi } from "@/api/eamApi";
import { REM_CONFIG } from "@/utils/fontSize";
const AssetDept = ({ className = "" }) => {
    const chartRef = useRef(null);
    const timerRef = useRef(null);
    const [chartData, setChartData] = useState([]);
    const departments = chartData.map((item) => item.cname) || [];
    const quantityData = chartData.map((item) => item.count) || [];
    useEffect(() => {
        getData();
    }, []);
    /**
     * @description: getData - 获取数据
     */
    const getData = async () => {
        try {
            const res = await eamApi.dept();
            setChartData(res.data);
        }
        catch (error) {
            console.error("获取部门数据失败:", error);
            // 可以设置默认数据或错误处理
        }
    };
    // 自动滚动配置
    const scrollConfig = {
        speed: 0.6,
        visibleCount: 4,
        loop: true,
        interval: 250,
    };
    // ECharts 配置项 - 改为函数形式，确保每次数据更新时重新计算
    const getOption = () => ({
        dataZoom: [
            {
                type: "inside",
                xAxisIndex: 0,
                start: 0,
                end: departments.length > 0
                    ? Math.min((scrollConfig.visibleCount / departments.length) * 100, 100)
                    : 100,
                show: false,
                zoomLock: true,
                moveOnMouseWheel: false,
            },
        ],
        xAxis: {
            type: "category",
            data: departments,
            axisLabel: {
                color: "#ffffff",
                fontSize: REM_CONFIG.fontSize.small,
                interval: 0,
                formatter: (value) => {
                    let result = "";
                    for (let i = 0; i < value.length; i++) {
                        if (i !== 0 && i % 4 === 0) {
                            result += "\n";
                        }
                        result += value[i];
                    }
                    return result;
                },
            },
            axisLine: { lineStyle: { color: "#446b9f" } },
            axisTick: { show: false },
        },
        yAxis: {
            type: "value",
            name: "数量（项）",
            nameTextStyle: {
                color: "#fff",
                fontSize: REM_CONFIG.fontSize.small,
            },
            min: 0,
            axisLabel: {
                color: "#fff",
                fontSize: REM_CONFIG.fontSize.small,
                formatter: "{value}",
            },
            axisLine: { lineStyle: { color: "#446b9f" } },
            splitLine: {
                lineStyle: { color: "rgba(68, 107, 159, 0.3)", type: "dashed" },
            },
        },
        series: [
            {
                type: "bar",
                data: quantityData,
                itemStyle: {
                    color: {
                        type: "linear",
                        x: 0,
                        y: 0,
                        x2: 0,
                        y2: 1,
                        colorStops: [
                            { offset: 0, color: "#53a9ff" },
                            { offset: 1, color: "#1f65b7" },
                        ],
                    },
                    borderRadius: [9, 9, 0, 0],
                },
                barWidth: "18%",
            },
        ],
        grid: {
            left: "4%",
            right: "4%",
            bottom: "0%",
            top: "18%",
            containLabel: true,
        },
        tooltip: {
            trigger: "axis",
            backgroundColor: "rgba(7, 31, 75, 0.8)",
            borderColor: "#446b9f",
            textStyle: { color: "#fff" },
            formatter: function (params) {
                const param = params[0];
                return `${param.name}: ${param.value} 个`;
            },
        },
    });
    // 自动滚动逻辑实现
    const startAutoScroll = (chart) => {
        if (timerRef.current) {
            clearInterval(timerRef.current);
        }
        // 如果没有数据或数据不足以滚动，则不启动轮播
        if (departments.length <= scrollConfig.visibleCount) {
            console.log("数据不足，不启动轮播");
            return;
        }
        let currentStart = 0;
        const totalCount = departments.length;
        const visiblePercent = (scrollConfig.visibleCount / totalCount) * 100;
        const maxStart = 100 - visiblePercent;
        console.log(`启动轮播: 总数=${totalCount}, 可视=${visiblePercent}%, 最大起始=${maxStart}%`);
        timerRef.current = setInterval(() => {
            currentStart += scrollConfig.speed;
            if (scrollConfig.loop && currentStart >= maxStart) {
                currentStart = 0;
            }
            else if (!scrollConfig.loop && currentStart >= maxStart) {
                currentStart = maxStart;
                if (timerRef.current) {
                    clearInterval(timerRef.current);
                }
                return;
            }
            chart.dispatchAction({
                type: "dataZoom",
                start: currentStart,
                end: currentStart + visiblePercent,
                dataZoomIndex: 0,
            });
        }, scrollConfig.interval);
    };
    // 初始化图表并启动自动滚动
    useEffect(() => {
        // 当数据加载完成后启动轮播
        if (chartData.length > 0) {
            const chartInstance = chartRef.current?.getEchartsInstance();
            if (chartInstance) {
                // 确保图表渲染完成后再启动轮播
                setTimeout(() => {
                    startAutoScroll(chartInstance);
                }, 300);
            }
        }
        return () => {
            if (timerRef.current) {
                clearInterval(timerRef.current);
            }
        };
    }, [chartData]); // 依赖chartData数组，数据变化时重新启动轮播
    return (_jsxs("div", { className: `
        relative 
        bg-gradient-to-t 
        from-[rgba(7,31,75,0.5)] 
        to-[rgba(6,32,66,0.5)] 
       
        flex flex-col 
        ${className}
      `, children: [_jsx(ModuleHeader, { title: "\u4F7F\u7528\u90E8\u95E8\u5206\u5E03" }), _jsx("div", { className: "flex-1", style: { minHeight: 0 }, children: _jsx(ReactEcharts, { ref: chartRef, option: getOption(), style: { width: "100%", height: "100%" }, opts: { renderer: "canvas" }, onChartReady: (chart) => {
                        // 图表准备好后的回调
                        if (chartData.length > 0) {
                            setTimeout(() => {
                                startAutoScroll(chart);
                            }, 300);
                        }
                    } }) })] }));
};
export default AssetDept;
